/**
 * 결과 확인 페이지 (18-24시)
 *
 * 역할:
 * - 사용자가 설문했던 인스타 ID와 숫자로 로그인
 * - 매칭 결과 확인 (매칭 성공 / 실패)
 * - 매칭 성공 시 상대방 인스타 ID 표시
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as S from "./ResultCheckPage.styles";
import smallBasicLion from "../assets/images/lion/small-basic-lion.webp";
import NotTimeModal from "../components/modal/NotTimeModal";
import api from "../api/axios";
import {
  validateAndFormatInstaId,
  isValidInstaId,
  isValidVerificationPin,
  isBlackListedId,
} from "../utils/validation";

const ResultCheckPage = () => {
  const [instaId, setInstaId] = useState(""); // 인스타 ID
  const [userNum, setUserNum] = useState(""); // 본인확인 숫자
  const [isModalOpen, setIsModalOpen] = useState(false); // 결과 모달
  const [errorMsg, setErrorMsg] = useState(""); // 입력 에러
  const [isLoading, setIsLoading] = useState(false); // API 호출 중
  const [isNotTimeOpen, setIsNotTimeOpen] = useState(false); //모달 추가
  const [modalContent, setModalContent] = useState("");

  const navigate = useNavigate();

  // 입력값이 모두 유효한지 확인
  const isFormValid =
    isValidInstaId(instaId) && isValidVerificationPin(userNum);

  /**
   * 매칭 결과 조회 함수
   *
   * 과정:
   * 1. 입력값 유효성 확인
   * 2. API 호출
   * 3. 매칭 결과에 따라 페이지 이동
   *    - 성공: /match-success (상대방 ID와 함께)
   *    - 실패: /match-fail
   */
  const handleResult = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      // 1. 로그인 API로 설문 참여 여부(isComplete) 먼저 확인
      const loginRes = await api.post("/api/onboarding/instagram", {
        instagramId: instaId,
        verificationPin: userNum,
      });

      if (loginRes.data.isSuccess) {
        const { isComplete, accessToken } = loginRes.data.result;
        localStorage.setItem("accessToken", accessToken);

        // 2. [미참여자] 모달 띄우기
        if (!isComplete) {
          setModalContent(`오늘 참여한 기록이 없어요!\n내일 다시 만나요~`);
          setIsNotTimeOpen(true);
          setIsLoading(false);
          return;
        }

        // 3. [참여자] 매칭 결과 조회
        const matchRes = await api.post("/api/match/result", {
          instagramId: instaId,
          verificationPin: userNum,
        });

        if (matchRes.data.isSuccess) {
          const { isMatched, partnerInstagramId } = matchRes.data.result;
          if (isMatched) {
            navigate("/match-success", {
              state: { instagramId: partnerInstagramId },
            });
          } else {
            navigate("/match-fail");
          }
        }
      }
    } catch (error) {
      const errorCode = error.response?.data?.code;
      const errorMessage = error.response?.data?.message;
      if (errorCode === "USER_4001" || errorCode === "USER_4011") {
        alert(errorMessage || "정보가 일치하지 않습니다.");
      } else {
        alert("결과 조회 중 오류가 발생했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInstaIdChange = (e) => {
    const formatted = validateAndFormatInstaId(e.target.value);
    setInstaId(formatted);

    if (isBlackListedId(formatted)) {
      setErrorMsg("사용할 수 없는 아이디입니다.");
    } else {
      setErrorMsg("");
    }
  };

  const handleUserNumChange = (e) => {
    const value = e.target.value;
    const onlyNumber = value.replace(/[^0-9]/g, "").slice(0, 4);
    setUserNum(onlyNumber);
  };

  return (
    <S.Container>
      <S.Content>
        <S.Img src={smallBasicLion} />
        <S.ResultTitle>
          결과 확인을 위해 <br />
          본인 확인을 해주세요!
        </S.ResultTitle>
        <S.InputBox
          type="text"
          placeholder="ex) @likelion_inu"
          value={instaId}
          onChange={handleInstaIdChange}
          onFocus={() => {
            if (!instaId) setInstaId("@");
          }}
        />
        {errorMsg ? (
          <S.GuideText $isError={true}>{errorMsg}</S.GuideText>
        ) : (
          <S.GuideText>당신은 행운아 ~ </S.GuideText>
        )}
        <S.InputBox
          type="text"
          placeholder="ex) 1234"
          value={userNum}
          onChange={handleUserNumChange}
        />

        <S.GuideText> 캡처 화면을 들고 멋사 부스로 와주세요! </S.GuideText>
        <S.Button onClick={handleResult} disabled={!isFormValid || isLoading}>
          {isLoading ? "로딩 중..." : "결과 확인하기"}
        </S.Button>
      </S.Content>
      <NotTimeModal
        isOpen={isNotTimeOpen}
        onClose={() => setIsNotTimeOpen(false)}
      >
        {modalContent}
      </NotTimeModal>
    </S.Container>
  );
};

export default ResultCheckPage;
